import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, List, Map, RotateCcw, Settings, Star, Zap } from "lucide-react";
import SpiralIcon from "@/components/SpiralIcon";
import StudySession from "@/components/StudySession";
import { getJourneyProgress, getWordsByWeek, saveSessionResults } from "@/lib/api";
import { haptic } from "@/lib/haptic";
import type { WeekStage } from "@/lib/api";
import type { SwipeResult, Word } from "@/types";
import { TOTAL_ENTRIES, TOTAL_WEEKS, WORDS_PER_WEEK, stageX, weekMeta } from "@/lib/course";
import type { WeekMeta } from "@/lib/course";


export default function Progress() {
  const navigate = useNavigate();
  const [weeks, setWeeks] = useState<WeekStage[]>([]);
  const [currentWeek, setCurrentWeek] = useState(1);
  const [loading, setLoading] = useState(true);
  const [reviewSession, setReviewSession] = useState<{ week: number; words: Word[] } | null>(null);
  const [reviewLoading, setReviewLoading] = useState<number | null>(null);
  const currentRef = useRef<HTMLDivElement>(null);

  const handleStartReview = async (week: number) => {
    setReviewLoading(week);
    const words = await getWordsByWeek(week);
    setReviewLoading(null);
    if (words.length > 0) setReviewSession({ week, words });
  };

  const handleReviewComplete = async (results: Record<string, SwipeResult>) => {
    await saveSessionResults(results, 0, { skipStreak: true });
    setReviewSession(null);
  };

  useEffect(() => {
    getJourneyProgress().then((data) => {
      if (data) {
        const effectiveWeek = data.currentWeek > TOTAL_WEEKS ? TOTAL_WEEKS + 1 : data.currentWeek;
        setCurrentWeek(effectiveWeek);
        if (data.currentWeek > TOTAL_WEEKS) {
          setWeeks(
            Array.from({ length: TOTAL_WEEKS }, (_, i) => ({
              week: i + 1,
              status: "completed",
              completedTasks: 1,
              totalTasks: 1,
            } as WeekStage)),
          );
        } else {
          setWeeks(data.weeks);
        }
      } else {
        setWeeks(
          Array.from({ length: TOTAL_WEEKS }, (_, i) => ({
            week: i + 1,
            status: i === 0 ? "current" : "locked",
            completedTasks: 0,
            totalTasks: 0,
          } as WeekStage)),
        );
      }
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!loading && currentRef.current) {
      setTimeout(() => {
        currentRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 400);
    }
  }, [loading]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (reviewSession) {
    return (
      <StudySession
        taskId={`review-w${reviewSession.week}`}
        taskType="REVIEW_G"
        words={reviewSession.words}
        enableDailyGoal={false}
        onDailyGoalMet={() => {}}
        onSessionEnd={handleReviewComplete}
        onInterrupt={() => setReviewSession(null)}
      />
    );
  }

  return (
    <div className="bg-[#0b0f1a] text-white" style={{ minHeight: "100dvh" }}>
      <header className="pt-safe px-5 pb-3 flex items-center justify-between sticky top-0 z-10 bg-[#0b0f1a]/90 backdrop-blur-sm border-b border-white/5">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg">
            <SpiralIcon className="w-3.5 h-5" color1="white" color2="white" pairColor="white" />
          </div>
          <span className="text-base font-black tracking-tighter italic text-blue-400">locomoco</span>
        </div>
        <p className="text-xs font-bold text-slate-400 tracking-widest">{TOTAL_WEEKS}週間の旅</p>
      </header>

      <div className="px-5 py-4 text-center">
        <p className="text-slate-400 text-sm">目標達成まで</p>
        <div className="flex items-center justify-center gap-2 mt-1">
          <span className="text-4xl font-black text-white">{TOTAL_ENTRIES}</span>
          <span className="text-lg font-bold text-blue-400">単語</span>
          <span className="text-slate-500 text-sm">/ {TOTAL_WEEKS}週間</span>
        </div>
        <div className="mt-3 h-2 bg-white/10 rounded-full overflow-hidden mx-8">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(100, (currentWeek / TOTAL_WEEKS) * 100)}%` }}
            transition={{ duration: 1, ease: "easeOut", delay: 0.3 }}
            className={`h-full rounded-full ${
              currentWeek > TOTAL_WEEKS
                ? "bg-gradient-to-r from-yellow-400 to-amber-500"
                : "bg-gradient-to-r from-blue-500 to-indigo-500"
            }`}
          />
        </div>
        <p className="text-xs text-slate-500 mt-1">
          {currentWeek > TOTAL_WEEKS
            ? `${TOTAL_ENTRIES} / ${TOTAL_ENTRIES}語 制覇！🏆`
            : `${Math.min(currentWeek * WORDS_PER_WEEK, TOTAL_ENTRIES)} / ${TOTAL_ENTRIES}語 解放済み`}
        </p>
      </div>

      <div className="relative px-5 py-6 pb-4">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)",
            backgroundSize: "24px 24px",
          }}
        />

        <div className="relative max-w-sm mx-auto">
          <div className="flex justify-start mb-2 ml-4">
            <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-xl border border-white/10">
              <span className="text-lg">🚩</span>
              <span className="text-xs font-bold text-slate-400">スタート</span>
            </div>
          </div>

          <ConnectingPath
            fromX="left"
            toX={stageX(1)}
            completed={weeks[0]?.status === "completed" || weeks[0]?.status === "current"}
            locked={weeks[0]?.status === "locked"}
          />

          {weeks.map((stage, idx) => {
            const xPos = stageX(stage.week);
            const meta = weekMeta(stage.week);
            const isCurrent = stage.status === "current";
            const nextStage = weeks[idx + 1];

            return (
              <div key={stage.week} className="relative">
                <div
                  ref={isCurrent ? currentRef : undefined}
                  className={`flex ${
                    xPos === "left" ? "justify-start" : xPos === "right" ? "justify-end" : "justify-center"
                  } mb-2`}
                >
                  <StageNode
                    stage={stage}
                    meta={meta}
                    isCurrent={isCurrent}
                    onReview={() => handleStartReview(stage.week)}
                    onViewWords={() => navigate(`/progress/words/${stage.week}`)}
                    reviewLoading={reviewLoading === stage.week}
                  />
                </div>

                {nextStage && (
                  <ConnectingPath
                    fromX={xPos}
                    toX={stageX(nextStage.week)}
                    completed={stage.status === "completed"}
                    locked={nextStage.status === "locked"}
                  />
                )}
              </div>
            );
          })}

          <ConnectingPath
            fromX={stageX(TOTAL_WEEKS)}
            toX="center"
            completed={weeks[TOTAL_WEEKS - 1]?.status === "completed"}
            locked={weeks[TOTAL_WEEKS - 1]?.status !== "completed"}
          />
          <div className="flex justify-center mt-2">
            <div
              className={`flex items-center gap-2 px-5 py-3 rounded-2xl border ${
                weeks[TOTAL_WEEKS - 1]?.status === "completed"
                  ? "bg-yellow-500/20 border-yellow-500/50 text-yellow-300"
                  : "bg-white/5 border-white/10 text-slate-500"
              }`}
            >
              <span className="text-xl">{weeks[TOTAL_WEEKS - 1]?.status === "completed" ? "🏆" : "🔒"}</span>
              <div>
                <p className="text-xs font-black">{TOTAL_ENTRIES}語 制覇</p>
                <p className="text-[10px] opacity-70">最終ゴール</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ScheduleGuide />
      <div className="h-28" />

      <div className="fixed bottom-0 left-0 right-0 pb-safe px-6 bg-gradient-to-t from-[#0b0f1a] to-transparent pointer-events-none">
        <nav className="max-w-sm mx-auto bg-slate-900/90 backdrop-blur-2xl border border-white/10 rounded-full p-1.5 flex items-center justify-around shadow-2xl pointer-events-auto">
          <button
            onClick={() => {
              haptic(10);
              navigate("/");
            }}
            className="p-3 text-slate-400 hover:text-blue-400 active:scale-90 transition-all"
          >
            <BookOpen size={22} />
          </button>
          <button className="p-3 bg-blue-600 text-white rounded-full shadow-lg shadow-blue-600/30">
            <Map size={22} />
          </button>
          <button
            onClick={() => {
              haptic(10);
              navigate("/settings");
            }}
            className="p-3 text-slate-400 hover:text-blue-400 active:scale-90 transition-all"
          >
            <Settings size={22} />
          </button>
        </nav>
      </div>
    </div>
  );
}

function ScheduleGuide() {
  // 7日目の復習は直近3週分のローリング方式。週が進んでも対象量は最大300語で頭打ちになる。
  const weeks = [
    { w: 1, day: "7日目", review: "おやすみ 😴", color: "text-slate-400", bar: 0 },
    { w: 2, day: "14日目", review: `第1週 ${WORDS_PER_WEEK}語を復習`, color: "text-blue-400", bar: 1 },
    { w: 3, day: "21日目", review: `第1〜2週 ${WORDS_PER_WEEK * 2}語を復習`, color: "text-indigo-400", bar: 2 },
    { w: 4, day: "28日目", review: `第1〜3週 ${WORDS_PER_WEEK * 3}語を復習`, color: "text-violet-400", bar: 3 },
    { w: 5, day: "35日目〜", review: `直近3週 ${WORDS_PER_WEEK * 3}語のローリング`, color: "text-yellow-400", bar: 3 },
  ];

  return (
    <div className="px-5 space-y-4">
      <div className="flex items-center gap-2.5 pt-2">
        <div className="h-px flex-1 bg-white/10" />
        <span className="text-[10px] font-black tracking-[0.25em] text-slate-500">学習スケジュール</span>
        <div className="h-px flex-1 bg-white/10" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl border border-blue-500/30 bg-blue-500/10 p-4">
          <p className="text-[10px] font-black text-blue-400 tracking-wider mb-2">1〜6日目</p>
          <p className="text-lg font-black text-white leading-tight">インプット</p>
          <p className="text-[11px] text-slate-400 mt-1 leading-snug">
            今週の新しい<span className="text-white font-bold">{WORDS_PER_WEEK}単語</span>を毎日30分くり返し学習
          </p>
        </div>
        <div className="rounded-2xl border border-violet-500/30 bg-violet-500/10 p-4">
          <p className="text-[10px] font-black text-violet-400 tracking-wider mb-2">7日目</p>
          <p className="text-lg font-black text-white leading-tight">スパイラル復習</p>
          <p className="text-[11px] text-slate-400 mt-1 leading-snug">
            直近3週分の単語（最大{WORDS_PER_WEEK * 3}語）＋フォーカス単語を一気に総復習
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10">
        <span className="text-xl">😴</span>
        <p className="text-[11px] text-slate-400 leading-snug">
          <span className="text-white font-bold">第1週の7日目だけはおやすみ。</span>{" "}
          何もしなくてもストリークは自動で維持されます。
        </p>
      </div>

      <div className="rounded-2xl border border-white/10 overflow-hidden">
        <div className="px-4 py-2.5 bg-white/5 border-b border-white/10">
          <p className="text-[10px] font-black text-slate-400 tracking-wider">週ごとの7日目復習量</p>
        </div>
        {weeks.map((row) => (
          <div key={row.w} className="px-4 py-3 border-b border-white/5 last:border-0">
            <div className="flex items-center justify-between gap-3 mb-1.5">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black text-slate-500 w-8">第{row.w}週</span>
                <span className="text-[10px] text-slate-500">{row.day}</span>
              </div>
              <span className={`text-[10px] font-black ${row.color}`}>{row.review}</span>
            </div>
            <div className="h-1 bg-white/10 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  row.bar === 0
                    ? "w-0"
                    : row.bar === 1
                    ? "bg-blue-500 w-1/4"
                    : row.bar === 2
                    ? "bg-indigo-500 w-2/4"
                    : "bg-violet-500 w-3/4"
                }`}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-yellow-500/30 bg-yellow-500/10 p-4">
        <div className="flex items-start gap-2.5">
          <span className="text-xl shrink-0">💡</span>
          <div>
            <p className="text-sm font-black text-yellow-300 mb-1">7日目の復習について</p>
            <p className="text-[11px] text-slate-400 leading-relaxed">
              7日目の復習は直近3週分（最大{WORDS_PER_WEEK * 3}語）のローリング方式です。第2週以降はこれが毎週くり返され、苦手な単語（フォーカス単語）が自動で上積みされます。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function StageNode({
  stage,
  meta,
  isCurrent,
  onReview,
  onViewWords,
  reviewLoading,
}: {
  stage: WeekStage;
  meta: WeekMeta;
  isCurrent: boolean;
  onReview?: () => void;
  onViewWords?: () => void;
  reviewLoading?: boolean;
}) {
  const isCompleted = stage.status === "completed";
  const isUpcoming = stage.status === "locked"; // 未到達の週。閲覧はできる。
  const progress =
    stage.totalTasks > 0 ? Math.round((stage.completedTasks / stage.totalTasks) * 100) : 0;
  const stars = isCompleted ? (progress >= 90 ? 3 : progress >= 50 ? 2 : 1) : 0;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.7 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, delay: (stage.week - 1) * 0.1 }}
      className="relative"
    >
      {isCurrent && (
        <motion.div
          animate={{ y: [0, -4, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap"
        >
          <div className="bg-blue-500 text-white text-[10px] font-black px-3 py-1 rounded-full shadow-lg shadow-blue-500/40">
            ★ 今ここ！
          </div>
          <div className="w-2 h-2 bg-blue-500 rotate-45 mx-auto -mt-1" />
        </motion.div>
      )}

      <div
        className={`relative w-[160px] rounded-3xl border-2 overflow-hidden transition-all ${
          isCompleted
            ? "bg-emerald-950/60 border-emerald-500/60 shadow-lg shadow-emerald-500/20"
            : isCurrent
            ? "bg-blue-950/80 border-blue-400 shadow-xl shadow-blue-500/30"
            : "bg-white/5 border-white/10"
        }`}
      >
        {isCurrent && (
          <motion.div
            animate={{ opacity: [0.3, 0.6, 0.3] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="absolute inset-0 bg-blue-500/10 pointer-events-none"
          />
        )}

        <div className="p-4 flex flex-col items-center text-center">
          <div className="flex items-center justify-center gap-1.5 mb-2">
            <span className={`text-2xl ${isUpcoming ? "opacity-60" : ""}`}>{meta.emoji}</span>
            {isCompleted && (
              <div className="flex gap-0.5">
                {Array.from({ length: 3 }, (_, i) => (
                  <Star
                    key={i}
                    size={12}
                    className={i < stars ? "text-yellow-400 fill-yellow-400" : "text-slate-600"}
                  />
                ))}
              </div>
            )}
          </div>

          <p className={`font-black text-sm ${isUpcoming ? "text-slate-300" : "text-white"}`}>
            {meta.title}
          </p>
          <p
            className={`text-[10px] mt-0.5 ${
              isUpcoming ? "text-slate-500" : isCompleted ? "text-emerald-400" : "text-slate-400"
            }`}
          >
            {meta.subtitle}
          </p>

          {isCurrent && stage.totalTasks > 0 && (
            <div className="mt-3 w-full">
              <div className="flex justify-between text-[10px] text-slate-400 mb-1">
                <span>
                  {stage.completedTasks}/{stage.totalTasks} 完了
                </span>
                <span>{progress}%</span>
              </div>
              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  className="h-full bg-blue-400 rounded-full"
                />
              </div>
            </div>
          )}

          <div
            className={`mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
              isCompleted
                ? "bg-emerald-500/20 text-emerald-400"
                : isCurrent
                ? "bg-blue-500/20 text-blue-300"
                : "bg-white/5 text-slate-500"
            }`}
          >
            {isCompleted ? (
              <>
                <Zap size={9} />
                クリア
              </>
            ) : isCurrent ? (
              <>学習中</>
            ) : (
              <>
                <BookOpen size={9} />
                未着手
              </>
            )}
          </div>

          {(onViewWords || onReview) && (
            <div className="mt-2.5 space-y-1.5 w-full">
              {onViewWords && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    haptic(10);
                    onViewWords();
                  }}
                  className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl bg-gradient-to-r from-violet-500/20 to-blue-500/20 border border-violet-500/30 active:scale-[0.98] transition-all text-[10px] font-black text-violet-200"
                >
                  <List size={11} />
                  単語一覧
                </button>
              )}
              {onReview && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    haptic(12);
                    onReview();
                  }}
                  disabled={reviewLoading}
                  className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-xl bg-white/10 active:bg-white/20 transition-colors text-[10px] font-bold text-slate-300 disabled:opacity-60"
                >
                  {reviewLoading ? (
                    <span className="w-3 h-3 border-2 border-slate-300 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <RotateCcw size={10} />
                  )}
                  復習する
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function ConnectingPath({
  fromX,
  toX,
  completed,
  locked,
}: {
  fromX: "left" | "center" | "right";
  toX: "left" | "center" | "right";
  completed: boolean;
  locked: boolean;
}) {
  return (
    <div className="relative h-12 flex items-center">
      <div
        className={`flex items-center justify-between w-full px-4 ${
          fromX === toX
            ? "flex-col gap-1"
            : fromX === "left"
            ? "justify-start pl-20"
            : "justify-end pr-20"
        }`}
      >
        <svg width="100%" height="48" className="absolute inset-0">
          <line
            x1={fromX === "left" ? "22%" : fromX === "right" ? "78%" : "50%"}
            y1="0"
            x2={toX === "left" ? "22%" : toX === "right" ? "78%" : "50%"}
            y2="48"
            stroke={completed ? "#10b981" : locked ? "#334155" : "#3b82f6"}
            strokeWidth="2.5"
            strokeDasharray={locked ? "6,5" : "none"}
            strokeLinecap="round"
          />
          {!locked && !completed && (
            <motion.circle
              r="4"
              fill="#60a5fa"
              animate={{
                cx: [
                  fromX === "left" ? "22%" : fromX === "right" ? "78%" : "50%",
                  toX === "left" ? "22%" : toX === "right" ? "78%" : "50%",
                ],
                cy: [0, 48],
              }}
              transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
            />
          )}
        </svg>
      </div>
    </div>
  );
}
